import { useState, useEffect } from "react";
import InputMask from 'react-input-mask';
import { useAppSelector } from "../../store/hooks";
import api from "../../services/api";
import { toast } from "react-toastify";

export type TipoChavePix = 'cpf' | 'cnpj' | 'email' | 'telefone' | 'aleatoria';

interface ConfiguracaoTabProps {
    empresaId: string;
    initialMessage: string;
    initialChavePix?: string;
    initialMessageManutencao?: string;
    rastreadorAtivo?: boolean;
    initialComissaoApurarSomenteSePago?: boolean;
}

export function detectarTipoChavePix(chave: string): TipoChavePix {
    if (!chave) return 'cpf';
    const limpa = chave.replace(/[.\-\/\s\(\)+]/g, '');

    if (chave.includes('@')) return 'email';
    if (chave.startsWith('+55') || (limpa.length === 13 && limpa.startsWith('55'))) return 'telefone';
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(chave.trim())) return 'aleatoria';
    if (/^\d+$/.test(limpa)) {
        if (limpa.length === 14) return 'cnpj';
        if (limpa.length === 11) return 'cpf';
    }
    // If it has mask characters but is numeric underneath
    const apenasDigitos = chave.replace(/\D/g, '');
    if (apenasDigitos.length === 14) return 'cnpj';
    if (apenasDigitos.length === 11) return 'cpf';

    return 'aleatoria';
}

export function formatarChaveParaExibicao(tipo: TipoChavePix, chave: string): string {
    if (!chave) return '';
    const digitos = chave.replace(/\D/g, '');

    switch (tipo) {
        case 'cpf':
            if (digitos.length === 11) {
                return `${digitos.slice(0,3)}.${digitos.slice(3,6)}.${digitos.slice(6,9)}-${digitos.slice(9)}`;
            }
            return chave;
        case 'cnpj':
            if (digitos.length === 14) {
                return `${digitos.slice(0,2)}.${digitos.slice(2,5)}.${digitos.slice(5,8)}/${digitos.slice(8,12)}-${digitos.slice(12)}`;
            }
            return chave;
        case 'telefone':
            // Remove +55 prefix if stored clean
            let tel = digitos;
            if (tel.startsWith('55') && tel.length === 13) {
                tel = tel.slice(2);
            }
            if (tel.length === 11) {
                return `+55 (${tel.slice(0,2)}) ${tel.slice(2,7)}-${tel.slice(7)}`;
            }
            return chave;
        default:
            return chave;
    }
}

export function limparChavePix(tipo: TipoChavePix, chave: string): string {
    switch (tipo) {
        case 'cpf':
        case 'cnpj':
            return chave.replace(/[.\-\/]/g, '');
        case 'telefone': {
            const digitos = chave.replace(/\D/g, '');
            if (digitos.startsWith('55')) return `+${digitos}`;
            return `+55${digitos}`;
        }
        case 'email':
            return chave.trim();
        case 'aleatoria':
            return chave.trim();
        default:
            return chave;
    }
}

export function validarChavePix(tipo: TipoChavePix, chave: string): string | null {
    if (!chave || !chave.trim()) return 'Informe a chave PIX';

    const digitos = chave.replace(/\D/g, '');

    switch (tipo) {
        case 'cpf':
            if (digitos.length !== 11) return 'CPF deve ter 11 digitos';
            return null;
        case 'cnpj':
            if (digitos.length !== 14) return 'CNPJ deve ter 14 digitos';
            return null;
        case 'email':
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(chave.trim())) return 'E-mail invalido';
            return null;
        case 'telefone': {
            let tel = digitos;
            if (tel.startsWith('55')) tel = tel.slice(2);
            if (tel.length !== 11) return 'Telefone deve ter DDD + 9 digitos (ex: 11999999999)';
            return null;
        }
        case 'aleatoria':
            if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(chave.trim())) {
                return 'Chave aleatoria deve estar no formato UUID (ex: 123e4567-e89b-12d3-a456-426614174000)';
            }
            return null;
        default:
            return null;
    }
}

const tiposChavePix: { value: TipoChavePix; label: string }[] = [
    { value: 'cpf', label: 'CPF' },
    { value: 'cnpj', label: 'CNPJ' },
    { value: 'email', label: 'E-mail' },
    { value: 'telefone', label: 'Telefone' },
    { value: 'aleatoria', label: 'Chave Aleatoria' },
];

export const ConfiguracaoTab: React.FC<ConfiguracaoTabProps> = ({
    empresaId,
    initialMessage,
    initialChavePix = '',
    initialMessageManutencao = '',
    rastreadorAtivo = false,
    initialComissaoApurarSomenteSePago = true,
}) => {
    const [activeSubTab, setActiveSubTab] = useState('whatsapp');
    const [comissaoApurarSomenteSePago, setComissaoApurarSomenteSePago] = useState<boolean>(initialComissaoApurarSomenteSePago);
    const [loadingComissao, setLoadingComissao] = useState(false);
    const [mensagemCobranca, setMensagemCobranca] = useState(initialMessage);
    const [mensagemManutencao, setMensagemManutencao] = useState(initialMessageManutencao);
    const [loadingManutencao, setLoadingManutencao] = useState(false);
    const [tipoChavePix, setTipoChavePix] = useState<TipoChavePix>('cpf');
    const [chavePix, setChavePix] = useState('');
    const [erroChavePix, setErroChavePix] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingPix, setLoadingPix] = useState(false);
    const token = useAppSelector(state => state.token);

    useEffect(() => {
        if (initialChavePix) {
            const tipoDetectado = detectarTipoChavePix(initialChavePix);
            setTipoChavePix(tipoDetectado);
            setChavePix(formatarChaveParaExibicao(tipoDetectado, initialChavePix));
        }
    }, [initialChavePix]);

    const handleTipoChange = (novoTipo: TipoChavePix) => {
        setTipoChavePix(novoTipo);
        setChavePix('');
        setErroChavePix(null);
    };

    const handleSaveMessage = async () => {
        try {
            setLoading(true);
            await api.put(`/ConfiguracaoEmpresa/AtualizarMensagemWhatsApp/${empresaId}`, mensagemCobranca, {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            toast.success('Mensagem de cobranca atualizada com sucesso!');
        } catch (error: any) {
            const errorMessage = error.response?.data?.Mensagem || 'Erro ao salvar a mensagem';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSavePix = async () => {
        const erro = validarChavePix(tipoChavePix, chavePix);
        if (erro) {
            setErroChavePix(erro);
            return;
        }

        const chaveLimpa = limparChavePix(tipoChavePix, chavePix);

        try {
            setLoadingPix(true);
            await api.put(`/ConfiguracaoEmpresa/AtualizarChavePix/${empresaId}`, JSON.stringify(chaveLimpa), {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            toast.success('Chave PIX atualizada com sucesso!');
            setErroChavePix(null);
        } catch (error: any) {
            const errorMessage = error.response?.data?.Mensagem || 'Erro ao salvar a chave PIX';
            toast.error(errorMessage);
        } finally {
            setLoadingPix(false);
        }
    };

    const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        if (value.length <= 500) {
            setMensagemCobranca(value);
        }
    };

    const handleManutencaoChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        if (value.length <= 500) {
            setMensagemManutencao(value);
        }
    };

    const handleSaveManutencao = async () => {
        try {
            setLoadingManutencao(true);
            await api.put(`/ConfiguracaoEmpresa/AtualizarMensagemWhatsAppManutencao/${empresaId}`, mensagemManutencao, {
                headers: { "Content-Type": "application/json" },
            });
            toast.success('Mensagem de manutenção atualizada com sucesso!');
        } catch (error: any) {
            toast.error(error.response?.data?.Mensagem || 'Erro ao salvar a mensagem');
        } finally {
            setLoadingManutencao(false);
        }
    };

    const handlePixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setChavePix(e.target.value);
        if (erroChavePix) {
            setErroChavePix(null);
        }
    };

    const isPixValido = !validarChavePix(tipoChavePix, chavePix);

    const handleSaveComissao = async () => {
        try {
            setLoadingComissao(true);
            await api.put(`/ConfiguracaoEmpresa/AtualizarComissaoApurarSomenteSePago/${empresaId}`, comissaoApurarSomenteSePago, {
                headers: { "Content-Type": "application/json" },
            });
            toast.success('Configuração de comissão atualizada com sucesso!');
        } catch (error: any) {
            toast.error(error.response?.data?.Mensagem || 'Erro ao salvar configuração de comissão');
        } finally {
            setLoadingComissao(false);
        }
    };

    const inputBaseClass = "w-full p-2 border rounded-md bg-white dark:bg-gray-800 text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent";
    const inputErrorClass = erroChavePix ? "border-red-500" : "border-gray-300";

    const renderPixInput = () => {
        switch (tipoChavePix) {
            case 'cpf':
                return (
                    <InputMask
                        mask="999.999.999-99"
                        value={chavePix}
                        onChange={handlePixChange}
                    >
                        {(inputProps: any) => (
                            <input
                                {...inputProps}
                                type="text"
                                className={`${inputBaseClass} ${inputErrorClass}`}
                                placeholder="000.000.000-00"
                            />
                        )}
                    </InputMask>
                );
            case 'cnpj':
                return (
                    <InputMask
                        mask="99.999.999/9999-99"
                        value={chavePix}
                        onChange={handlePixChange}
                    >
                        {(inputProps: any) => (
                            <input
                                {...inputProps}
                                type="text"
                                className={`${inputBaseClass} ${inputErrorClass}`}
                                placeholder="00.000.000/0000-00"
                            />
                        )}
                    </InputMask>
                );
            case 'telefone':
                return (
                    <InputMask
                        mask="+55 (99) 99999-9999"
                        value={chavePix}
                        onChange={handlePixChange}
                    >
                        {(inputProps: any) => (
                            <input
                                {...inputProps}
                                type="text"
                                className={`${inputBaseClass} ${inputErrorClass}`}
                                placeholder="+55 (00) 00000-0000"
                            />
                        )}
                    </InputMask>
                );
            case 'email':
                return (
                    <input
                        type="email"
                        value={chavePix}
                        onChange={handlePixChange}
                        className={`${inputBaseClass} ${inputErrorClass}`}
                        placeholder="exemplo@email.com"
                    />
                );
            case 'aleatoria':
                return (
                    <input
                        type="text"
                        value={chavePix}
                        onChange={handlePixChange}
                        className={`${inputBaseClass} ${inputErrorClass}`}
                        placeholder="123e4567-e89b-12d3-a456-426614174000"
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="bg-white dark:bg-gray-900 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-black dark:text-gray-100 mb-6">Configuracoes da Empresa</h2>

            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto">
                <button
                    className={`px-4 py-2 font-medium text-sm flex items-center whitespace-nowrap ${activeSubTab === 'whatsapp' ? 'text-[#1F467E] border-b-2 border-[#1F467E]' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                    onClick={() => setActiveSubTab('whatsapp')}
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    WhatsApp
                </button>
                <button
                    className={`px-4 py-2 font-medium text-sm flex items-center whitespace-nowrap ${activeSubTab === 'manutencao' ? 'text-[#1F467E] border-b-2 border-[#1F467E]' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                    onClick={() => setActiveSubTab('manutencao')}
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Manutenção
                </button>
                <button
                    className={`px-4 py-2 font-medium text-sm flex items-center whitespace-nowrap ${activeSubTab === 'pix' ? 'text-[#1F467E] border-b-2 border-[#1F467E]' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                    onClick={() => setActiveSubTab('pix')}
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Chave PIX
                </button>
                <button
                    className={`px-4 py-2 font-medium text-sm flex items-center whitespace-nowrap ${activeSubTab === 'comissao' ? 'text-[#1F467E] border-b-2 border-[#1F467E]' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                    onClick={() => setActiveSubTab('comissao')}
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Comissão
                </button>
            </div>

            {activeSubTab === 'whatsapp' && (
                <div className="space-y-6 max-w-3xl">
                    <div>
                        <h3 className="text-lg font-medium text-black dark:text-gray-100 mb-3">Mensagem de Cobranca</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Configure a mensagem padrao que sera enviada para os clientes quando for gerar um link de cobranca pelo WhatsApp.
                        </p>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-black dark:text-gray-200">
                                Mensagem (maximo 500 caracteres)
                            </label>
                            <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                <span className="font-medium text-black dark:text-white">Variaveis disponiveis:</span>{" "}
                                <span className="font-mono text-[#1F467E]">{`{nome_locatario}`}</span>,{" "}
                                <span className="font-mono text-[#1F467E]">{`{valor}`}</span>,{" "}
                                <span className="font-mono text-[#1F467E]">{`{vencimento}`}</span>,{" "}
                                <span className="font-mono text-[#1F467E]">{`{pix_copia_cola}`}</span>,{" "}
                                <span className="font-mono text-[#1F467E]">{`{chave_pix}`}</span>
                            </div>

                            <textarea
                                value={mensagemCobranca}
                                onChange={handleMessageChange}
                                rows={6}
                                className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent"
                                placeholder="Digite a mensagem de cobranca..."
                            />
                            <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
                                {mensagemCobranca.length}/500 caracteres
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={handleSaveMessage}
                            disabled={loading}
                            className="px-4 py-2 bg-[#1F467E] text-white rounded-md hover:bg-[#2a5a9e] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Salvando...' : 'Salvar Mensagem'}
                        </button>
                    </div>
                </div>
            )}

            {activeSubTab === 'manutencao' && (
                <div className="space-y-6 max-w-3xl">
                    {!rastreadorAtivo ? (
                        <div className="flex flex-col items-center justify-center py-10 text-center">
                            <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Integração com rastreador necessária</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mb-4">
                                Para configurar a mensagem de manutenção, é necessário primeiro ativar a integração com a <strong>Agillock</strong> na aba <strong>Integrações</strong>.
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                                Minha Conta → Integrações → Agillock
                            </p>
                        </div>
                    ) : (<>
                    <div>
                        <h3 className="text-lg font-medium text-black dark:text-gray-100 mb-3">Mensagem de Manutenção</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Configure a mensagem que será enviada ao locatário pelo WhatsApp quando houver uma manutenção pendente no veículo.
                        </p>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-black dark:text-gray-200">
                                Mensagem (máximo 500 caracteres)
                            </label>
                            <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                <span className="font-medium text-black dark:text-white">Variáveis disponíveis:</span>{" "}
                                <span className="font-mono text-[#1F467E]">{`{nome_locatario}`}</span>,{" "}
                                <span className="font-mono text-[#1F467E]">{`{placa}`}</span>,{" "}
                                <span className="font-mono text-[#1F467E]">{`{item}`}</span>,{" "}
                                <span className="font-mono text-[#1F467E]">{`{status}`}</span>,{" "}
                                <span className="font-mono text-[#1F467E]">{`{km_info}`}</span>
                            </div>

                            <textarea
                                value={mensagemManutencao}
                                onChange={handleManutencaoChange}
                                rows={6}
                                className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent"
                                placeholder="Olá {nome_locatario}! Informamos que a manutenção do veículo {placa} está {status}. Item: {item}. {km_info}. Por favor, entre em contato para agendarmos."
                            />
                            <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
                                {mensagemManutencao.length}/500 caracteres
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={handleSaveManutencao}
                            disabled={loadingManutencao}
                            className="px-4 py-2 bg-[#1F467E] text-white rounded-md hover:bg-[#2a5a9e] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loadingManutencao ? 'Salvando...' : 'Salvar Mensagem'}
                        </button>
                    </div>
                    </>)}
                </div>
            )}

            {activeSubTab === 'comissao' && (
                <div className="space-y-6 max-w-3xl">
                    <div>
                        <h3 className="text-lg font-medium text-black dark:text-gray-100 mb-3">Apuração de Comissões</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                            Configure como as comissões dos vendedores são apuradas no sistema.
                        </p>

                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-black dark:text-gray-200 mb-1">
                                        Apurar comissão somente quando pagamento for confirmado
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Quando ativo, comissões só contam após o pagamento ser marcado como pago. Quando inativo, são calculadas no momento da criação do lançamento.
                                    </p>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setComissaoApurarSomenteSePago(false)}
                                        className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${!comissaoApurarSomenteSePago ? 'bg-[#1F467E] text-white border-[#1F467E]' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-50'}`}
                                    >
                                        Não
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setComissaoApurarSomenteSePago(true)}
                                        className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${comissaoApurarSomenteSePago ? 'bg-[#1F467E] text-white border-[#1F467E]' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-50'}`}
                                    >
                                        Sim
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={handleSaveComissao}
                            disabled={loadingComissao}
                            className="px-4 py-2 bg-[#1F467E] text-white rounded-md hover:bg-[#2a5a9e] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loadingComissao ? 'Salvando...' : 'Salvar Configuração'}
                        </button>
                    </div>
                </div>
            )}

            {activeSubTab === 'pix' && (
                <div className="space-y-6 max-w-3xl">
                    <div>
                        <h3 className="text-lg font-medium text-black dark:text-gray-100 mb-3">Chave PIX</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Configure a chave PIX que sera utilizada para gerar os QR Codes de pagamento.
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-black dark:text-gray-200 mb-1">
                                    Tipo da Chave
                                </label>
                                <select
                                    value={tipoChavePix}
                                    onChange={(e) => handleTipoChange(e.target.value as TipoChavePix)}
                                    className="w-full p-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent"
                                >
                                    {tiposChavePix.map((tipo) => (
                                        <option key={tipo.value} value={tipo.value}>
                                            {tipo.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-black dark:text-gray-200 mb-1">
                                    Chave PIX
                                </label>
                                {renderPixInput()}
                                {erroChavePix && (
                                    <p className="mt-1 text-sm text-red-500">{erroChavePix}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={handleSavePix}
                            disabled={loadingPix || !chavePix.trim() || !isPixValido}
                            className="px-4 py-2 bg-[#1F467E] text-white rounded-md hover:bg-[#2a5a9e] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loadingPix ? 'Salvando...' : 'Salvar Chave PIX'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
